import {
  deleteToken,
  getToken,
  onMessage,
  type MessagePayload,
  type Unsubscribe,
} from 'firebase/messaging'
import {
  getFirebaseMessaging,
  getFirebaseVapidKey,
  getFirebaseWebConfig,
} from '@/services/firebase'

const FCM_SW_URL = '/firebase-messaging-sw.js'
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope'

export type FcmTokenResult =
  | { ok: true; token: string }
  | {
      ok: false
      reason:
        | 'unsupported'
        | 'missing_config'
        | 'permission_denied'
        | 'permission_dismissed'
        | 'sw_failed'
        | 'token_failed'
    }

let fcmSwRegistration: ServiceWorkerRegistration | null = null
let foregroundUnsub: Unsubscribe | null = null

export function isFcmConfigured(): boolean {
  return Boolean(getFirebaseWebConfig() && getFirebaseVapidKey())
}

/** Short, non-identifying fingerprint for logs — never the full token. */
export function fcmTokenFingerprint(token: string): string {
  if (!token) return ''
  return token.length <= 12 ? token : `${token.slice(0, 8)}…${token.slice(-4)}`
}

/** Targeted, low-volume diagnostics for token acquisition only — not routine spam. */
function logFcmDiagnostic(message: string, err?: unknown): void {
  if (err) {
    console.debug(`[fcm] ${message}:`, err instanceof Error ? err.message : err)
  } else {
    console.debug(`[fcm] ${message}`)
  }
}

/**
 * Registers the FCM service worker on an isolated scope so it does not replace
 * the existing `/sw.js` Web Push worker used by the current PMMS push channel.
 *
 * Do NOT await `navigator.serviceWorker.ready` here — that promise waits for a
 * worker that controls *this page*. The FCM scope
 * (`/firebase-cloud-messaging-push-scope`) never controls `/`, so `ready` hangs
 * forever when no other SW (e.g. `/sw.js`) is the controller.
 */
async function getFcmServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (fcmSwRegistration) return fcmSwRegistration
  if (!('serviceWorker' in navigator)) return null

  try {
    const registration = await navigator.serviceWorker.register(FCM_SW_URL, {
      scope: FCM_SW_SCOPE,
      updateViaCache: 'none',
    })
    // Pick up regenerated SW (onBackgroundMessage / click handlers) after deploys.
    void registration.update().catch(() => {})

    if (!registration.active) {
      const worker = registration.installing ?? registration.waiting
      if (worker) {
        await new Promise<void>((resolve, reject) => {
          const onStateChange = () => {
            if (worker.state === 'activated') {
              worker.removeEventListener('statechange', onStateChange)
              resolve()
            } else if (worker.state === 'redundant') {
              worker.removeEventListener('statechange', onStateChange)
              reject(new Error('FCM service worker became redundant before activation'))
            }
          }
          worker.addEventListener('statechange', onStateChange)
          if (worker.state === 'activated') {
            worker.removeEventListener('statechange', onStateChange)
            resolve()
          }
        })
      }
    }

    if (!registration.active) return null

    fcmSwRegistration = registration
    return fcmSwRegistration
  } catch {
    return null
  }
}

export type GetFcmTokenOptions = {
  /**
   * When true (default), may call Notification.requestPermission() if permission is `default`.
   * Production shell sync passes false (permission is ensured separately).
   */
  requestPermission?: boolean
}

/**
 * Obtains an FCM registration token.
 * Pass `{ requestPermission: false }` for silent production sync / logout cleanup.
 * Does not send the token to the backend — callers own registration.
 */
export async function getFcmToken(options?: GetFcmTokenOptions): Promise<FcmTokenResult> {
  const requestPermission = options?.requestPermission !== false

  if (typeof window === 'undefined') {
    return { ok: false, reason: 'unsupported' }
  }

  if (!isFcmConfigured()) {
    return { ok: false, reason: 'missing_config' }
  }

  const messaging = await getFirebaseMessaging()
  if (!messaging) {
    return { ok: false, reason: 'unsupported' }
  }

  const vapidKey = getFirebaseVapidKey()
  if (!vapidKey) {
    return { ok: false, reason: 'missing_config' }
  }

  let permission = Notification.permission
  if (permission === 'default') {
    if (!requestPermission) {
      return { ok: false, reason: 'permission_dismissed' }
    }
    permission = await Notification.requestPermission()
  }
  if (permission === 'denied') {
    return { ok: false, reason: 'permission_denied' }
  }
  if (permission !== 'granted') {
    return { ok: false, reason: 'permission_dismissed' }
  }

  const serviceWorkerRegistration = await getFcmServiceWorkerRegistration()
  if (!serviceWorkerRegistration) {
    logFcmDiagnostic('FCM service worker registration/activation failed')
    return { ok: false, reason: 'sw_failed' }
  }

  try {
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration,
    })
    if (!token) {
      return { ok: false, reason: 'token_failed' }
    }
    return { ok: true, token }
  } catch (err) {
    // Common self-recovering case: a push subscription already exists on this
    // SW registration under a stale/mismatched applicationServerKey (e.g. after
    // a VAPID key rotation, or a leftover subscription from a prior deploy).
    // Chrome throws (often InvalidAccessError) instead of just returning a new
    // token, and getToken() never resolves it on its own — the browser used to
    // need a manual "clear site data" to unstick this. Unsubscribe the stale
    // subscription on our own SW registration (never the legacy /sw.js one)
    // and retry getToken() once before giving up.
    logFcmDiagnostic('getToken failed, attempting stale-subscription recovery', err)
    try {
      const existing = await serviceWorkerRegistration.pushManager.getSubscription()
      if (existing) await existing.unsubscribe()
      const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration })
      if (!token) return { ok: false, reason: 'token_failed' }
      logFcmDiagnostic('stale-subscription recovery succeeded')
      return { ok: true, token }
    } catch (retryErr) {
      logFcmDiagnostic('stale-subscription recovery failed', retryErr)
      return { ok: false, reason: 'token_failed' }
    }
  }
}

/**
 * INVALID-TOKEN RECOVERY ONLY. Call after backend `status: 'token_invalid'`.
 * Unsubscribes the FCM-scope push subscription and deletes the Firebase token,
 * then mints a new one. Does NOT unregister the SW (that left Chrome unable to
 * getToken until a full site-data clear).
 */
export async function regenerateFcmToken(): Promise<FcmTokenResult> {
  const messaging = await getFirebaseMessaging()
  if (!messaging) return { ok: false, reason: 'unsupported' }

  try {
    const registration =
      fcmSwRegistration ??
      (await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE))
    if (registration) {
      const existing = await registration.pushManager.getSubscription()
      if (existing) await existing.unsubscribe()
    }
  } catch (err) {
    logFcmDiagnostic('push unsubscribe during invalid-token recovery failed (continuing)', err)
  }

  try {
    await deleteToken(messaging)
  } catch (err) {
    logFcmDiagnostic('deleteToken during invalid-token recovery failed (continuing)', err)
  }

  // Drop cached registration handle so the next getToken re-resolves an active worker.
  fcmSwRegistration = null

  await new Promise((resolve) => setTimeout(resolve, 300))

  return getFcmToken({ requestPermission: false })
}

/**
 * Show an OS notification for a foreground FCM message.
 * Prefer `new Notification` on a focused desktop tab (most reliable), then SW.
 */
export async function showFcmOsNotification(payload: MessagePayload): Promise<void> {
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (Notification.permission !== 'granted') return

  const data = payload.data || {}
  const title = payload.notification?.title || data.title || 'Update'
  const body = payload.notification?.body || data.body || ''
  const tag = data.notificationId || data.tag || undefined
  const rawUrl = data.url || data.path || '/notifications'
  let targetUrl: string
  try {
    targetUrl = new URL(rawUrl, window.location.origin).href
  } catch {
    targetUrl = `${window.location.origin}/notifications`
  }
  const icon = data.icon || `${window.location.origin}/icons/icon-192.png`

  const options = {
    body,
    icon,
    tag,
    renotify: Boolean(tag),
    silent: false,
    data: { url: targetUrl },
  } as NotificationOptions & { renotify?: boolean }

  // Focused desktop tabs: Notification ctor is more reliable than an FCM-scoped SW.
  try {
    const note = new Notification(title, options)
    note.onclick = () => {
      window.focus()
      try {
        const abs = new URL(targetUrl, window.location.origin)
        const next = abs.pathname + abs.search + abs.hash
        if (
          abs.origin === window.location.origin &&
          `${window.location.pathname}${window.location.search}${window.location.hash}` !== next
        ) {
          window.location.assign(next)
        } else if (abs.href !== window.location.href) {
          window.location.assign(abs.href)
        }
      } catch {
        window.location.assign(targetUrl)
      }
      note.close()
    }
    return
  } catch {
    // Fall through to SW showNotification.
  }

  try {
    const registration = await getFcmServiceWorkerRegistration()
    if (registration) {
      await registration.showNotification(title, options)
    }
  } catch {
    // Best-effort — caller may also show an in-app toast.
  }
}

/**
 * Foreground FCM handler. Invokes callback for each message while the tab is open.
 * Callers (useFcmClient) are responsible for OS Notification / toast UI.
 */
export async function subscribeFcmForegroundMessages(
  onPayload?: (payload: MessagePayload) => void,
): Promise<Unsubscribe | null> {
  const messaging = await getFirebaseMessaging()
  if (!messaging) return null

  if (foregroundUnsub) {
    foregroundUnsub()
    foregroundUnsub = null
  }

  foregroundUnsub = onMessage(messaging, (payload) => {
    onPayload?.(payload)
  })

  return foregroundUnsub
}

export function unsubscribeFcmForegroundMessages(): void {
  foregroundUnsub?.()
  foregroundUnsub = null
}
